import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FormPropertyProps } from "../../../FormProperty";
import { DataListOption } from "src/api/sure-api/dataList/useGetAllDataList";
import { FoodsSelectTriggerAdapter } from "src/components/EntityForm/FoodsSelectTriggerAdapter";
import { FoodsTreeSelectField } from "src/components/EntityForm/FoodsTreeSelectField";
import { useQuestionnaireFormContext } from "src/components/QuestionnaireForm/context/useQuestionnaireForm";
import { usePropertyContext } from "../../../context/usePropertyContext";
import { FieldValueLayout } from "../../FieldValueLayout";
import { saveOrDeleteOptionAnswer } from "../saveOrDeleteOptionAnswer";
import {
    UNGROUPED_OPTION_KEY,
    filterGroupedDataListOptionsByLabel,
    findOptionByCode,
    getOptionGroupKey,
    getPropertyDefaultOption,
    getPropertyGenericOption,
    type GroupedDataListOptions,
} from "src/utils/dataListOptions";

type SelectTreeViewProps = FormPropertyProps & {
    groupedOptions: GroupedDataListOptions;
    options: DataListOption[];
};

type OptionTreeNode = {
    id: string;
    label: string;
    parentId: string | null;
    option: DataListOption | null;
};

const GROUPED_SELECT_SEARCH_MIN_OPTIONS = 12;

const itemIdFromOption = (option: DataListOption): string =>
    `${getOptionGroupKey(option)}-${option.value}-${option.position}`;

const getNodeId = (node: OptionTreeNode) => node.id;
const getNodeLabel = (node: OptionTreeNode) => node.label;
const getNodeParentId = (node: OptionTreeNode) => node.parentId;
const isOptionNode = (node: OptionTreeNode) => node.option !== null;

const buildTreeNodes = (groups: GroupedDataListOptions): OptionTreeNode[] =>
    Object.entries(groups).flatMap(([groupKey, group]) => {
        const groupId = groupKey === UNGROUPED_OPTION_KEY ? null : `group:${groupKey}`;
        const optionNodes = group.options.map((option) => ({
            id: itemIdFromOption(option),
            label: option.label,
            parentId: groupId,
            option,
        }));

        return groupId === null ? optionNodes : (
                [{ id: groupId, label: group.label, parentId: null, option: null }, ...optionNodes]
            );
    });

export const SelectTreeView = (props: SelectTreeViewProps) => {
    const { t } = useTranslation();
    const {
        groupedOptions,
        property,
        options,
        position,
        dimensionValue,
        showSubscore,
        accessibleLabel,
        descriptionId,
        invalid,
    } = props;
    const { appearance = "legacy" } = useQuestionnaireFormContext();

    const { post, getElement, onUpdate, onDelete, canEdit } = usePropertyContext();
    const element = getElement(property.id, position);

    const [genericValueSelected, setGenericValueSelected] = useState<boolean>(element?.isGenericValue ?? false);
    const [search, setSearch] = useState("");
    const defaultValueOption = getPropertyDefaultOption(property, options) ?? null;
    const genericValueOption = getPropertyGenericOption(property, options) ?? null;

    const selectedCode = element?.dataListOptionCodes?.[0];
    const initialValue: DataListOption | undefined = findOptionByCode(options, selectedCode);
    const [selectedOption, setSelectedOption] = useState<DataListOption | null>(initialValue ?? null);

    useEffect(() => {
        setGenericValueSelected(element?.isGenericValue ?? false);
        const selectedCode = element?.dataListOptionCodes?.[0];
        const updatedValue: DataListOption | undefined = findOptionByCode(options, selectedCode);
        setSelectedOption(updatedValue ?? null);
    }, [element, options]);

    const allNodes = useMemo(() => buildTreeNodes(groupedOptions), [groupedOptions]);
    const visibleNodes = useMemo(
        () => buildTreeNodes(filterGroupedDataListOptionsByLabel(groupedOptions, search)),
        [groupedOptions, search],
    );
    const selectedNode =
        selectedOption === null ? null : (allNodes.find((node) => node.option?.id === selectedOption.id) ?? null);

    if (post === undefined) return;

    const updatePropertyValue = (value: DataListOption | null, isGenericValue?: boolean) => {
        saveOrDeleteOptionAnswer(
            { onUpdate, onDelete },
            { postId: post.id, propertyId: Number(property.id), position: Number(props.position ?? 0) },
            value,
            isGenericValue,
        );
    };

    const selectNode = (node: OptionTreeNode | null) => {
        if (node === null || node.option === null) return;

        setSelectedOption(node.option);
        updatePropertyValue(node.option);
    };

    const selectField = (
        <FoodsTreeSelectField<OptionTreeNode>
            selectedOption={selectedNode}
            accessibleLabel={accessibleLabel ?? property.title}
            options={visibleNodes}
            onChange={selectNode}
            getId={getNodeId}
            getName={getNodeLabel}
            getParentId={getNodeParentId}
            isSelectable={isOptionNode}
            placeholder={t("component.select.placeholder")}
            search={search}
            setSearch={setSearch}
            searchPlaceholder={t("component.select.searchplaceholder")}
            disableSearch={options.length < GROUPED_SELECT_SEARCH_MIN_OPTIONS}
            emptyOptionsText={t("component.select.nooptions")}
            noResultText={t("component.select.noresultfound")}
            disabled={canEdit === false || property.readonly || genericValueSelected}
        />
    );

    return (
        <FieldValueLayout
            property={property}
            element={element}
            layoutOptions={{
                defaultValue: defaultValueOption?.label ?? null,
                genericValue: genericValueOption?.label ?? null,
                isGenericValueSelected: genericValueSelected,
                dimensionValue,
                forceHideScore: showSubscore === false,
                setDefaultValue: () => {
                    if (defaultValueOption === null) return;
                    if (selectedOption?.value === defaultValueOption.value) {
                        return;
                    }

                    setSelectedOption(defaultValueOption);
                    updatePropertyValue(defaultValueOption);
                },
                setGenericValue: () => {
                    if (genericValueOption === null) return;

                    const newValue = !genericValueSelected ? genericValueOption : null;
                    setGenericValueSelected((prevState) => !prevState);
                    setSelectedOption(newValue);

                    updatePropertyValue(newValue, !genericValueSelected);
                },
                hideUnit: true,
            }}
        >
            {appearance === "product-v2" ?
                <FoodsSelectTriggerAdapter
                    accessibleLabel={accessibleLabel ?? property.title}
                    descriptionId={descriptionId}
                    invalid={invalid}
                >
                    {selectField}
                </FoodsSelectTriggerAdapter>
            :   selectField}
        </FieldValueLayout>
    );
};
